import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { Tables } from "@/integrations/supabase/types";
import { Sentry } from "@/lib/sentry";

type Profile = Tables<"profiles">;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  isAuthenticated: false,
  signOut: async () => {},
  refetchProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // The effective user: real session user only.
  // PR-AG removed the DEV_MOCK_USER bypass — dev now uses real Supabase auth like prod.
  const effectiveUser = session?.user ?? null;

  useEffect(() => {
    let mounted = true;
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!mounted) return;
        setSession(nextSession);
        setLoading(false);
        if (event === "SIGNED_OUT") {
          setProfile(null);
          queryClient.clear();
          Sentry.setUser(null);
        } else if (nextSession?.user) {
          // Only send user id — no email/name/phone (PII)
          Sentry.setUser({ id: nextSession.user.id });
        }
      }
    );

    supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setSession(data.session ?? null);
        setLoading(false);
      }
    }).catch(() => {
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Profile fetch
  useEffect(() => {
    if (!effectiveUser?.id) {
      setProfile(null);
      return;
    }

    supabase
      .from("profiles")
      .select("*")
      .eq("id", effectiveUser.id)
      .maybeSingle()
      .then(
        ({ data }) => {
          setProfile(data ?? null);
        },
        () => setProfile(null)
      );
  }, [effectiveUser?.id]);

  const signOut = async () => {
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000)),
      ]);
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setSession(null);
      setProfile(null);
      queryClient.clear();
    }
  };

  const refetchProfile = async () => {
    if (!effectiveUser?.id) return;
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", effectiveUser.id)
      .maybeSingle();
    if (data) setProfile(data);
  };

  const emailConfirmed = session?.user?.email_confirmed_at != null;
  const isAuthenticated = !!session?.user && emailConfirmed;

  return (
    <AuthContext.Provider
      value={{
        session,
        user: effectiveUser ?? null,
        profile,
        loading,
        isAuthenticated,
        signOut,
        refetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
